/* PAGINA 2 · DETTAGLIO BACKTEST.
 *
 * È l'unica schermata che può permettersi `GET /backtester/:id`, cioè la entity
 * completa con le serie (`trades`, `equityLinePoints`, `candlesClosePrices`):
 * su una riga sola i megabyte si pagano una volta, in lista sarebbero
 * megabyte × 50. In cambio le serie vanno ridotte PRIMA di disegnarle, non
 * dopo: ottantamila coppie in un `<path>` bloccano il thread principale.
 *
 * L'ordine della pagina è quello del prototipo, e non è casuale: prima il
 * verdetto — batte il mercato, e a che prezzo — poi le tre prove che dicono se
 * di quel verdetto ci si può fidare, e solo dopo i grafici e le operazioni.
 * Il profitto non compare mai da solo: la prima cosa a schermo lo mostra
 * insieme a drawdown, operazioni, periodo e rendimento del mercato.
 */

import { AnniControMercato } from '../../componenti/AnniControMercato';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  APariRischio,
  Avvertenza,
  Avvisi,
  AzioneDistruttiva,
  ContenitoreDati,
  Distintivo,
  DistintivoPeriodo,
  IconaFreccia,
  IconaStrategia,
  IntestazionePagina,
  NeiRibassi,
  Pulsante,
  ScheletroLista,
  Tenuta,
  Verdetto,
  divisioneDaMetodo,
  usaAvvisi,
} from '../../componenti';
import { backtest as apiBacktest } from '../../api/risorse';
import { usaRichiesta } from '../../api/usaRichiesta';
import {
  dataOra,
  durataPeriodo,
  num,
  periodo as etichettaPeriodo,
  timeframe,
} from '../../lib/formato';
import type { Backtest } from '../../api/tipi';
import { GraficoAnnuale } from './GraficoAnnuale';
import { RisultatoBacktest } from './RisultatoBacktest';
import { parametriPerModulo, payloadRiesecuzione } from './riesecuzione';
import { indirizzoScegliAltro } from './statoConfronto';
import './backtest.css';

export function DettaglioBacktest() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { avvisi, mostra } = usaAvvisi();

  const idNumerico = Number(id);
  const idValido = Number.isFinite(idNumerico) && idNumerico > 0;

  const richiesta = usaRichiesta(
    (segnale) => apiBacktest.uno(idNumerico, segnale),
    [idNumerico],
    { attiva: idValido },
  );

  const dato = richiesta.dati;

  /* Copie locali dei due campi modificabili: l'endpoint risponde con la riga
     aggiornata, ma ricaricare tutta la entity — serie comprese — per aver
     cambiato una stella significherebbe riscaricare megabyte. */
  const [preferito, setPreferito] = useState<boolean | null>(null);
  const [descrizione, setDescrizione] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState<'preferito' | 'descrizione' | 'metriche' | null>(null);
  const [inRiesecuzione, setInRiesecuzione] = useState(false);

  useEffect(() => {
    setPreferito(dato?.isFavorite ?? null);
    setDescrizione(dato?.description ?? null);
  }, [dato]);

  /* Un backtest in corso non ha metriche: senza ricarica la pagina resterebbe
     su uno stato che non arriverà mai da solo. Il polling si spegne appena il
     motore ha finito. */
  const inEsecuzione = dato?.status === 'RUNNING';
  const ricarica = richiesta.ricarica;
  useEffect(() => {
    if (!inEsecuzione) return;
    const t = setInterval(ricarica, 5000);
    return () => clearInterval(t);
  }, [inEsecuzione, ricarica]);

  const cambiaPreferito = useCallback(async () => {
    if (!dato) return;
    const nuovo = !(preferito ?? dato.isFavorite);
    setInCorso('preferito');
    try {
      /* Si passa il valore esplicito: senza corpo il server INVERTE quello che
         ha lui, e due tocchi rapidi finirebbero fuori sincrono. */
      await apiBacktest.segnaPreferito(dato.id, nuovo);
      setPreferito(nuovo);
      mostra(nuovo ? 'Aggiunto ai preferiti' : 'Tolto dai preferiti');
    } catch (e) {
      mostra(e instanceof Error ? e.message : 'Non riuscito', 'fallito');
    } finally {
      setInCorso(null);
    }
  }, [dato, preferito, mostra]);

  const salvaDescrizione = useCallback(async () => {
    if (!dato) return;
    setInCorso('descrizione');
    try {
      await apiBacktest.descrivi(dato.id, descrizione);
      mostra('Nota salvata');
    } catch (e) {
      mostra(e instanceof Error ? e.message : 'Non riuscito', 'fallito');
    } finally {
      setInCorso(null);
    }
  }, [dato, descrizione, mostra]);

  const ricalcola = useCallback(async () => {
    if (!dato) return;
    setInCorso('metriche');
    try {
      await apiBacktest.ricalcolaMetriche(dato.id);
      mostra('Metriche ricalcolate');
      ricarica();
    } catch (e) {
      mostra(e instanceof Error ? e.message : 'Ricalcolo non riuscito', 'fallito');
    } finally {
      setInCorso(null);
    }
  }, [dato, mostra, ricarica]);

  /**
   * Rifà il calcolo di QUESTA configurazione per ottenerne le serie.
   *
   * `force: true` non è un dettaglio: senza, il server riconosce che una riga
   * identica esiste già e restituisce quella — cioè la riga vuota da cui si è
   * partiti — e il pulsante sembrerebbe non fare niente.
   *
   * Finita la chiamata si VA sulla riga nuova invece di aggiornare questa.
   * Motivo: la riesecuzione crea un backtest con un id proprio, e mostrarlo
   * sotto l'indirizzo del vecchio farebbe mentire l'URL — un segnalibro o un
   * link condiviso riaprirebbero la riga vuota. Sulla riga nuova il polling
   * dello stato `RUNNING` che questa pagina ha già fa il resto: mostra «in
   * corso», si aggiorna ogni cinque secondi e riempie i grafici da sé.
   */
  const riesegui = useCallback(async () => {
    if (!dato) return;
    setInRiesecuzione(true);
    try {
      const nuovo = await apiBacktest.avvia(payloadRiesecuzione(dato));
      mostra(`Esecuzione avviata: backtest #${nuovo.id}`);
      navigate(`/backtest/${nuovo.id}`);
    } catch (e) {
      mostra(e instanceof Error ? e.message : 'Avvio non riuscito', 'fallito');
    } finally {
      setInRiesecuzione(false);
    }
  }, [dato, mostra, navigate]);

  const elimina = useCallback(async () => {
    if (!dato) return;
    try {
      await apiBacktest.elimina(dato.id);
      mostra('Backtest eliminato');
      navigate('/backtest');
    } catch (e) {
      mostra(e instanceof Error ? e.message : 'Eliminazione non riuscita', 'fallito');
    }
  }, [dato, mostra, navigate]);

  if (!idValido) {
    return (
      <div className="sb-cornice">
        <IntestazionePagina titolo="Dettaglio backtest" />
        <div className="sb-bt-dettaglio">
          <Avvertenza livello="grave">
            L'indirizzo non contiene un identificativo valido: «{id}» non è un numero.
          </Avvertenza>
        </div>
      </div>
    );
  }

  return (
    <div className="sb-cornice">
      <Avvisi avvisi={avvisi} />

      <div style={{ padding: 'var(--sb-pad-pagina)', paddingBottom: 0 }}>
        <Pulsante variante="nudo" onClick={() => navigate('/backtest')}>
          <IconaFreccia verso="sinistra" />
          Tutti i backtest
        </Pulsante>
      </div>

      <ContenitoreDati
        stato={richiesta.stato}
        /* `GET /backtester/:id` risponde 200 con corpo vuoto quando l'id non
           esiste: è un'assenza, non un errore, e va detta come tale. */
        causaVuoto={`Nessun backtest con identificativo ${idNumerico}. Può essere stato eliminato, oppure l'indirizzo è vecchio.`}
        azioneVuoto={{ etichetta: 'Torna alla lista', onClick: () => navigate('/backtest') }}
        messaggioErrore={richiesta.errore?.message}
        onRiprova={richiesta.ricarica}
        scheletro={
          <div className="sb-bt-dettaglio">
            <ScheletroLista righe={4} />
          </div>
        }
      >
        {dato && (
          <Corpo
            dato={dato}
            preferito={preferito ?? dato.isFavorite}
            descrizione={descrizione}
            onDescrizione={setDescrizione}
            onSalvaDescrizione={salvaDescrizione}
            onPreferito={cambiaPreferito}
            onRicalcola={ricalcola}
            onElimina={elimina}
            inCorso={inCorso}
            onCreaTradebot={() => navigate(`/tradebot/nuovo?daBacktest=${dato.id}`)}
            onDuplica={() => navigate(`/backtest/nuovo?${parametriPerModulo(dato)}`)}
            onConfronta={() => navigate(indirizzoScegliAltro(dato.id))}
            onRiesegui={riesegui}
            inRiesecuzione={inRiesecuzione}
          />
        )}
      </ContenitoreDati>
    </div>
  );
}

/* ========================================================================== */

interface PropsCorpo {
  dato: Backtest;
  preferito: boolean;
  descrizione: string | null;
  onDescrizione: (v: string) => void;
  onSalvaDescrizione: () => void;
  onPreferito: () => void;
  onRicalcola: () => void;
  onElimina: () => void;
  inCorso: 'preferito' | 'descrizione' | 'metriche' | null;
  onCreaTradebot: () => void;
  onDuplica: () => void;
  /**
   * Comincia un confronto da questa riga sola.
   *
   * Serve perché spuntare due caselle in una lista funziona quando le due righe
   * sono vicine, e su un milione e mezzo di righe quasi mai lo sono. Riporta
   * alla lista con questo backtest già nella selezione, liberi di filtrare per
   * trovare l'altro.
   */
  onConfronta: () => void;
  onRiesegui: () => void;
  inRiesecuzione: boolean;
}

function Corpo({
  dato,
  preferito,
  descrizione,
  onDescrizione,
  onSalvaDescrizione,
  onPreferito,
  onRicalcola,
  onElimina,
  inCorso,
  onCreaTradebot,
  onDuplica,
  onConfronta,
  onRiesegui,
  inRiesecuzione,
}: PropsCorpo) {
  const periodo = etichettaPeriodo(dato.startDate, dato.endDate);
  const durata = durataPeriodo(dato.startDate, dato.endDate);

  const parametri = Object.entries(dato.strategyParams ?? {});

  return (
    <>
      <div style={{ padding: 'var(--sb-pad-pagina)', borderBottom: '1px solid var(--sb-bordo-tenue)' }}>
        <div className="sb-bt-testata">
          <IconaStrategia nome={dato.strategyName} dim={38} />
          <div className="sb-bt-testata__corpo">
            <h1 className="sb-intestazione__titolo">{dato.strategyName}</h1>
            <div className="sb-bt-testata__sotto">
              {dato.asset}/{dato.currency} · {timeframe(dato.candleSize)} ·{' '}
              {dato.backtestGroupId == null ? 'senza gruppo' : `gruppo #${dato.backtestGroupId}`} ·
              backtest #{dato.id}
            </div>
            <div style={{ display: 'flex', gap: 'var(--sb-s-3)', flexWrap: 'wrap', marginTop: 'var(--sb-s-4)' }}>
              <DistintivoPeriodo>
                {periodo}
                {durata ? ` · ${durata}` : ''}
              </DistintivoPeriodo>
              {dato.status && (
                <Distintivo tono={dato.status === 'ERROR' ? 'perdita' : dato.status === 'RUNNING' ? 'cautela' : 'neutro'}>
                  {dato.status === 'RUNNING' ? 'in corso' : dato.status === 'ERROR' ? 'errore' : 'concluso'}
                </Distintivo>
              )}
              {preferito && <Distintivo tono="accento">preferito</Distintivo>}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 'var(--sb-s-3)', flexWrap: 'wrap' }}>
            <Pulsante onClick={onPreferito} disabled={inCorso === 'preferito'}>
              {preferito ? 'Togli dai preferiti' : 'Preferito'}
            </Pulsante>
            {/* «Rifai cambiando qualcosa»: porta al modulo già compilato con
                questi parametri, dove si cambia solo il campo che interessa. */}
            <Pulsante onClick={onDuplica}>Rifai cambiando qualcosa</Pulsante>
            {/* Il confronto parte anche da una riga sola: questo pulsante mette
                questo backtest nella selezione e riporta alla lista, dove i
                filtri servono a trovare l'altro. */}
            <Pulsante onClick={onConfronta}>Confronta con…</Pulsante>
            <Pulsante variante="primario" onClick={onCreaTradebot}>
              Crea tradebot
            </Pulsante>
          </div>
        </div>
      </div>

      <div className="sb-bt-dettaglio">
        {/* --- ciò che rende il risultato meno affidabile va detto SOPRA i
            numeri, non in fondo: sotto, si legge dopo aver già deciso. --- */}
        {dato.status === 'RUNNING' && (
          <Avvertenza livello="cautela">
            Il motore sta ancora calcolando: i numeri qui sotto sono parziali o assenti. La pagina
            si aggiorna da sola ogni cinque secondi.
          </Avvertenza>
        )}
        {dato.status === 'ERROR' && (
          <Avvertenza livello="grave">
            Il backtest si è interrotto con un errore: le metriche che vedi non descrivono il
            periodo intero.
          </Avvertenza>
        )}
        {dato.wasStoppedEarly && (
          <Avvertenza livello="cautela">
            Fermato prima della fine{dato.stopNotes ? `: ${dato.stopNotes}` : '.'} Il periodo
            misurato è più corto di quello richiesto.
          </Avvertenza>
        )}
        {dato.warmupWarning && <Avvertenza livello="cautela">{dato.warmupWarning}</Avvertenza>}

        {/* --- il verdetto: profitto, mercato, drawdown, operazioni, periodo --- */}
        <Verdetto
          profitto={dato.strategyChange}
          mercato={dato.marketChange}
          drawdown={dato.drawdownMaxValue}
          operazioni={dato.tradesNumber}
          periodo={periodo}
        />

        {/* --- le tre prove. Ognuna col suo qualificatore, sempre a schermo --- */}
        <div className="sb-griglia">
          <Tenuta
            valore={dato.walkForwardScore}
            divisione={divisioneDaMetodo(dato.walkForwardMetodo)}
            periodo={periodo}
          />
          <NeiRibassi
            valore={dato.bearYearsAvg}
            anniNegativi={dato.bearYearsCount}
            periodo={periodo}
          />
          <APariRischio
            profitto={dato.strategyChange}
            drawdown={dato.drawdownMaxValue}
            periodo={periodo}
          />
        </div>

        {/* --- robustezza del parametro: l'intorno --- */}
        {dato.intorno != null && (
          <div className="sb-bt-riquadro">
            <div className="sb-bt-riquadro__testata">
              <span className="sb-bt-riquadro__titolo">Intorno del parametro</span>
              <DistintivoPeriodo>{periodo}</DistintivoPeriodo>
            </div>
            <div className="sb-numero sb-tono-neutro" style={{ fontSize: 'var(--sb-t-26)' }}>
              {num(dato.intorno, 2)}
            </div>
            <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
              Quanto il risultato regge muovendo{' '}
              {dato.intornoParametro ? `«${dato.intornoParametro}»` : 'il parametro'}
              {dato.intornoAssi != null ? ` su ${num(dato.intornoAssi)} assi` : ''}. Un intorno
              basso significa che il numero dipende dall'aver azzeccato un valore: non è una
              strategia, è una coincidenza misurata.
            </div>
          </div>
        )}

        {/* --- anno per anno --- */}
        {dato.yearlyChange && dato.yearlyChange.length > 0 && (
          <div className="sb-bt-riquadro">
            <div className="sb-bt-riquadro__testata">
              <span className="sb-bt-riquadro__titolo">Anno per anno, contro il mercato</span>
              <span className="sb-bt-legenda">
                <span className="sb-bt-legenda__voce">
                  <span
                    className="sb-bt-legenda__segno"
                    style={{ background: 'var(--sb-guadagno)' }}
                  />
                  <span
                    className="sb-bt-legenda__segno"
                    style={{ background: 'var(--sb-perdita)' }}
                  />
                  strategia
                </span>
                <span className="sb-bt-legenda__voce">
                  <span
                    className="sb-bt-legenda__segno"
                    style={{ background: 'var(--sb-bordo-marcato)' }}
                  />
                  mercato
                </span>
              </span>
            </div>
            {/* Il grafico e l'elenco stanno insieme, e non è una ripetizione: il grafico dice
                la FORMA — dove cadono gli anni buoni, quanto è profondo il peggiore, se il
                vantaggio sul mercato viene da un anno solo — e l'elenco dice le CIFRE, in
                ordine e confrontabili a due decimali. La lista scorre solo l'elenco perché lì
                lo spazio non c'è; qui c'è, e togliere il grafico è stato l'errore che il
                committente ha chiesto di rimediare. */}
            <GraficoAnnuale anni={dato.yearlyChange} />
            <div style={{ marginTop: 'var(--sb-s-6)' }}>
              <AnniControMercato anni={dato.yearlyChange} densita="ariosa" />
            </div>
            <div className="sb-nota" style={{ marginTop: 'var(--sb-s-5)' }}>
              L'altezza delle barre è compressa con la radice del valore: gli anni a tre cifre
              non schiacciano gli altri contro lo zero. Sotto ogni anno stanno scritti il
              rendimento della strategia, quello del mercato e la differenza in punti.
            </div>
            <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
              Nell'elenco il <b>colore del testo</b> dice il segno della strategia; il{' '}
              <b>grassetto</b> e lo <b>sfondo della riga</b> dicono che ha battuto il mercato, e
              valgono anche fra due numeri negativi — perdere meno è vincere. Lo sfondo è più
              intenso dove il distacco è maggiore, e non dice se il mercato è sceso: un anno con
              il mercato a −60% e la strategia a +12% è verde, perché è andata benissimo. Fra
              parentesi il rendimento del mercato nello stesso anno.
            </div>
          </div>
        )}

        {/* --- il risultato: prezzo con le operazioni marcate, drawdown allineato,
            capitale nel tempo ed elenco dei trade. E' lo stesso componente che
            monta il backtest libero: una riga guardata dalla libreria e una
            appena eseguita sono lo stesso oggetto, e due copie divergerebbero
            al primo campo aggiunto. Quando le serie mancano -- il caso delle
            righe nate da una griglia -- e' lui a spiegarlo e a offrire la
            riesecuzione. --- */}
        <RisultatoBacktest
          dato={dato}
          periodo={periodo}
          onRiesegui={onRiesegui}
          inRiesecuzione={inRiesecuzione}
        />

        {/* --- parametri --- */}
        {parametri.length > 0 && (
          <div className="sb-bt-riquadro">
            <div className="sb-bt-riquadro__testata">
              <span className="sb-bt-riquadro__titolo">Parametri usati</span>
              <Distintivo contorno>
                warmup {num(dato.warmupPeriod)} · candela {timeframe(dato.candleSize)}
              </Distintivo>
            </div>
            <div className="sb-bt-parametri">
              {parametri.map(([k, v]) => (
                <div className="sb-bt-parametro" key={k}>
                  <div className="sb-bt-parametro__chiave">{k}</div>
                  <div className="sb-bt-parametro__valore">
                    {v == null ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* --- nota personale --- */}
        <div className="sb-bt-riquadro">
          <div className="sb-bt-riquadro__testata">
            <span className="sb-bt-riquadro__titolo">Nota</span>
            <span className="sb-nota">creato il {dataOra(dato.createdAt)}</span>
          </div>
          <textarea
            className="sb-campo__input"
            rows={3}
            value={descrizione ?? ''}
            placeholder="Perché questa configurazione è stata tenuta, o scartata."
            onChange={(e) => onDescrizione(e.target.value)}
            style={{
              width: '100%',
              background: 'var(--sb-superficie-2)',
              border: '1px solid var(--sb-bordo)',
              borderRadius: 'var(--sb-r-3)',
              padding: 'var(--sb-s-4)',
              resize: 'vertical',
            }}
          />
          <div style={{ marginTop: 'var(--sb-s-5)' }}>
            <Pulsante
              onClick={onSalvaDescrizione}
              disabled={inCorso === 'descrizione' || (descrizione ?? '') === (dato.description ?? '')}
            >
              Salva la nota
            </Pulsante>
          </div>
        </div>

        {/* --- manutenzione e cancellazione, in fondo e a due tocchi --- */}
        <div className="sb-bt-riquadro">
          <div className="sb-bt-riquadro__testata">
            <span className="sb-bt-riquadro__titolo">Manutenzione</span>
          </div>
          <Pulsante variante="tenue" onClick={onRicalcola} disabled={inCorso === 'metriche'}>
            {inCorso === 'metriche' ? 'Ricalcolo in corso…' : 'Ricalcola le metriche'}
          </Pulsante>
          {/* Il ricalcolo è sincrono e senza timeout lato server: può durare
              minuti, e chi preme deve saperlo prima, non scoprirlo aspettando. */}
          <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
            Rilegge le serie e ricalcola tenuta, nei ribassi e le metriche derivate. La chiamata è
            sincrona e su un backtest lungo può durare minuti: la pagina resta ferma finché non
            risponde.
          </div>

          <hr className="sb-divisore" />

          <AzioneDistruttiva
            etichetta="Elimina questo backtest"
            etichettaArmata="Premi di nuovo per eliminare"
            nota="Sparisce dal magazzino con le sue serie. Un tradebot già creato da questa configurazione continua a operare: non viene toccato."
            onConferma={onElimina}
          />
        </div>
      </div>
    </>
  );
}
